import { z } from 'zod'

import type { RateSnapshot } from './rate-snapshot'
import type { User } from './user'
import { ModuleTypeEnum } from './module-type'
import { ResolvedFromValues } from './rate-snapshot'
import { TimeEntryCategorySummarySchema } from './time-category'

// TimeEntry status enum
export enum TimeEntryStatusEnum {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CHANGES_REQUESTED = 'CHANGES_REQUESTED',
  COMPLETED = 'COMPLETED',
  ON_HOLD = 'ON_HOLD',
  CANCELED = 'CANCELED',
}

// TimeEntry approval action enum
export enum TimeEntryApprovalActionEnum {
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CHANGES_REQUESTED = 'CHANGES_REQUESTED',
  RECALLED = 'RECALLED',
  EDITED = 'EDITED',
  RESUBMITTED = 'RESUBMITTED',
  ADMIN_STATUS_CHANGE = 'ADMIN_STATUS_CHANGE',
}

// TimeEntry entry type enum
export enum TimeEntryTypeEnum {
  TIMER = 'TIMER', // Start/end time based tracking
  MANUAL = 'MANUAL', // Duration-based manual entry
}

export enum TimeEntryRoleSourceEnum {
  SELECTED = 'SELECTED',
  PRIMARY = 'PRIMARY',
  LEGACY = 'LEGACY',
  // Auto-resolved at write time when the entry's billing scope exposes exactly
  // one configured role and the member has no explicit/primary/legacy role.
  CONFIGURED = 'CONFIGURED',
}

// TimeEntry sort field enum
export enum TimeEntrySortFieldEnum {
  DESCRIPTION = 'description',
  STATUS = 'status',
  START_TIME = 'startTime',
  END_TIME = 'endTime',
  BILLABLE = 'billable',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
}

// TimeEntryTag status enum
export enum TimeEntryTagStatusEnum {
  ACTIVE = 'ACTIVE',
  DISMISSED = 'DISMISSED',
  REMOVED = 'REMOVED',
}

// TimeEntryTag action enum
export enum TimeEntryTagActionEnum {
  TAGGED = 'TAGGED',
  DISMISSED = 'DISMISSED',
  REMOVED = 'REMOVED',
  DUPLICATED = 'DUPLICATED',
}

// User subset for time entries - only the essential fields needed for display
const TimeEntryUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  displayName: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
})

// Project subset for time entries - only name and id needed for display
const TimeEntryProjectSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
})

// Entity subset for time entries - only name and id needed for display
const TimeEntryEntitySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
})

// Tagged member schema for time entries - for member tagging/notification
const TimeEntryTaggedMemberSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
  avatar: z.string().nullable().optional(),
  role: z.string(),
})

const TimeEntryLinkedModuleSchema = z.object({
  moduleType: z.union([z.nativeEnum(ModuleTypeEnum), z.literal('TASK_GROUP')]),
  moduleId: z.string().uuid(),
  moduleName: z.string().nullable().optional(),
  // Members tagged for the time tracked against this item. Persisted on the
  // running timer so the finish dialog can seed its rows; absent means
  // "untouched" (older clients), empty means "nobody".
  taggedUserIds: z.array(z.string().uuid()).max(50).optional(),
})

const TimeEntrySplitItemSchema = z.object({
  moduleType: z.union([z.nativeEnum(ModuleTypeEnum), z.literal('TASK_GROUP')]),
  moduleId: z.string().uuid(),
  moduleName: z.string().nullable().optional(),
  durationMinutes: z.number().finite().positive(),
  // Optional per-split description. Falls back to the parent timer's
  // description when not provided.
  description: z.string().nullable().optional(),
  // Optional members to tag on the time entry created for this split.
  taggedUserIds: z.array(z.string().uuid()).max(50).optional(),
})

export const SplitTimeEntryInputSchema = z.object({
  splits: z.array(TimeEntrySplitItemSchema).min(1).max(100),
  removeEntryIds: z.array(z.string().uuid()).max(100).optional(),
})

export const SwitchTimerItemInputSchema = z.object({
  moduleType: z.union([z.nativeEnum(ModuleTypeEnum), z.literal('TASK_GROUP')]),
  moduleId: z.string().uuid(),
  moduleName: z.string().nullable().optional(),
  linkedModules: z.array(TimeEntryLinkedModuleSchema).optional(),
  description: z.string().nullable().optional(),
  billable: z.boolean().optional(),
  categoryId: z.string().uuid().nullable().optional(),
  // Optional role the member is performing the work under for the new item.
  // Used when the member has multiple eligible roles on the billing entity
  // and wants the switched-to timer to resolve rates against a specific role.
  roleId: z.string().uuid().nullable().optional(),
})

// Approver user subset for time entries
const TimeEntryApproverSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  displayName: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
})

// Base TimeEntry schema without refinement
const BaseTimeEntrySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  userId: z.string().uuid(),
  user: TimeEntryUserSchema.optional(), // Include user data for display
  description: z.string(),
  status: z.string().default(TimeEntryStatusEnum.DRAFT),
  entryType: z.nativeEnum(TimeEntryTypeEnum).default(TimeEntryTypeEnum.TIMER),
  entryDate: z.date(), // The date this time entry is for
  startTime: z.date().nullable().optional(), // Optional for manual time entry
  endTime: z.date().nullable().optional(), // Optional for live timers and manual entry
  duration: z.number().nullable().optional(), // Duration in hours for manual entry
  billable: z.boolean(),
  // Kind of work (Client work, Admin, Training…); null = uncategorized.
  categoryId: z.string().uuid().nullable().optional(),
  category: TimeEntryCategorySummarySchema.nullable().optional(),
  timerSessionId: z.string().uuid().nullable().optional(),
  moduleType: z.string().nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  deliverableId: z.string().uuid().nullable().optional(),
  moduleName: z.string().nullable().optional(), // Store module name directly for performance and data integrity
  // Related data - project and entity information when available
  project: TimeEntryProjectSchema.optional(), // Include project data when time entry is related to a project
  entity: TimeEntryEntitySchema.optional(), // Include entity data when time entry is related to an entity
  // Tagged members for notifications
  taggedMembers: z.array(TimeEntryTaggedMemberSchema).optional(), // Members tagged for notification about this time entry
  // Linked modules for multi-reference timers
  linkedModules: z.array(TimeEntryLinkedModuleSchema).optional(),
  // Timer persistence fields
  isPaused: z.boolean().default(false),
  pausedAt: z.date().nullable().optional(),
  pausedDuration: z.number().default(0), // Total paused time in seconds
  lastHeartbeat: z.date().nullable().optional(),
  // Approval workflow fields
  submittedAt: z.date().nullable().optional(),
  approvedAt: z.date().nullable().optional(),
  approvedBy: z.string().uuid().nullable().optional(),
  approver: TimeEntryApproverSchema.optional(), // Include approver data for display
  // rejectedAt/rejectedBy/rejectionReason are shared by both REJECTED and CHANGES_REQUESTED statuses.
  // The entry's `status` field distinguishes which action was taken.
  rejectedAt: z.date().nullable().optional(),
  rejectedBy: z.string().uuid().nullable().optional(),
  rejecter: TimeEntryApproverSchema.optional(), // Include rejecter/reviewer data for display
  rejectionReason: z.string().nullable().optional(),
  // Version tracking for edit-after-approval
  version: z.number().default(1),
  previousVersionId: z.string().uuid().nullable().optional(),
  // Pay period relation
  payPeriodId: z.string().uuid().nullable().optional(),
  // Rate fields (populated by reports endpoint or stored on entry via resolveAndStoreRates)
  roleId: z.string().uuid().nullable().optional(),
  role: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  roleSource: z.nativeEnum(TimeEntryRoleSourceEnum).nullable().optional(),
  billRate: z.number().nullable().optional(),
  payRate: z.number().nullable().optional(),
  costRate: z.number().nullable().optional(),
  resolvedFrom: z.enum(ResolvedFromValues).nullable().optional(),
  ratesResolvedAt: z.date().nullable().optional(),
  rateSnapshot: z.custom<RateSnapshot>().nullable().optional(),
  billableHours: z.number().nullable().optional(),
  billableAmount: z.number().nullable().optional(),
  costAmount: z.number().nullable().optional(),
  payAmount: z.number().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
})

// TimeEntry schema with validation
export const TimeEntrySchema = BaseTimeEntrySchema.refine(
  data => {
    // Validation: Either have startTime (for timer) or duration (for manual entry)
    if (data.entryType === TimeEntryTypeEnum.TIMER) {
      return data.startTime != null // Timer entries must have start time
    } else if (data.entryType === TimeEntryTypeEnum.MANUAL) {
      return data.duration != null && data.duration > 0 // Manual entries must have positive duration
    }
    return true
  },
  {
    message:
      'Timer entries must have a start time, manual entries must have a positive duration',
  },
)

export type TimeEntry = z.infer<typeof TimeEntrySchema>
export type TimeEntryTaggedMember = z.infer<typeof TimeEntryTaggedMemberSchema>
export type TimeEntryLinkedModule = z.infer<typeof TimeEntryLinkedModuleSchema>
export type TimeEntrySplitItem = z.infer<typeof TimeEntrySplitItemSchema>
export type SplitTimeEntryInput = z.infer<typeof SplitTimeEntryInputSchema>
export type SwitchTimerItemInput = z.infer<typeof SwitchTimerItemInputSchema>

// Create TimeEntry schema
export const CreateTimeEntrySchema = BaseTimeEntrySchema.omit({
  id: true,
  organizationId: true,
  user: true, // Exclude user from create schema
  project: true, // Exclude project - this is computed/fetched
  entity: true, // Exclude entity - this is computed/fetched
  category: true, // Computed from categoryId
  approver: true, // Exclude approver - computed
  rejecter: true, // Exclude rejecter - computed
  submittedAt: true, // Server-managed
  approvedAt: true, // Server-managed
  approvedBy: true, // Server-managed
  rejectedAt: true, // Server-managed
  rejectedBy: true, // Server-managed
  rejectionReason: true, // Server-managed
  version: true, // Server-managed
  previousVersionId: true, // Server-managed
  payPeriodId: true, // Server-managed
  billRate: true, // Server-managed (resolveAndStoreRates)
  payRate: true, // Server-managed (resolveAndStoreRates)
  costRate: true, // Server-managed (resolveAndStoreRates)
  roleSource: true, // Server-managed (resolveAndStoreRates)
  resolvedFrom: true, // Server-managed (resolveAndStoreRates)
  ratesResolvedAt: true, // Server-managed (resolveAndStoreRates)
  rateSnapshot: true, // Server-managed (resolveAndStoreRates)
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
  .extend({
    // Stable write identity; a replay after a lost response returns the saved entry.
    operationId: z.string().uuid().optional(),
  })
  .refine(
    data => {
      // Same validation for create schema
      if (data.entryType === TimeEntryTypeEnum.TIMER) {
        return data.startTime != null
      } else if (data.entryType === TimeEntryTypeEnum.MANUAL) {
        return data.duration != null && data.duration > 0
      }
      return true
    },
    {
      message:
        'Timer entries must have a start time, manual entries must have a positive duration',
    },
  )

export type CreateTimeEntry = z.infer<typeof CreateTimeEntrySchema>

// Update TimeEntry schema - base schema for updates without validation
const BaseUpdateTimeEntrySchema = BaseTimeEntrySchema.omit({
  id: true,
  organizationId: true,
  user: true, // Exclude user from update schema
  project: true, // Exclude project - this is computed/fetched
  entity: true, // Exclude entity - this is computed/fetched
  category: true, // Computed from categoryId
  approver: true, // Exclude approver - computed
  rejecter: true, // Exclude rejecter - computed
  submittedAt: true, // Server-managed
  approvedAt: true, // Server-managed
  approvedBy: true, // Server-managed
  rejectedAt: true, // Server-managed
  rejectedBy: true, // Server-managed
  rejectionReason: true, // Server-managed
  version: true, // Server-managed
  previousVersionId: true, // Server-managed
  payPeriodId: true, // Server-managed
  billRate: true, // Server-managed (resolveAndStoreRates)
  payRate: true, // Server-managed (resolveAndStoreRates)
  costRate: true, // Server-managed (resolveAndStoreRates)
  roleSource: true, // Server-managed (resolveAndStoreRates)
  resolvedFrom: true, // Server-managed (resolveAndStoreRates)
  ratesResolvedAt: true, // Server-managed (resolveAndStoreRates)
  rateSnapshot: true, // Server-managed (resolveAndStoreRates)
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
  .partial()
  .extend({
    // Stable write identity; a replay after a lost response is a no-op.
    operationId: z.string().uuid().optional(),
  })

export const UpdateTimeEntrySchema = BaseUpdateTimeEntrySchema

export type UpdateTimeEntry = z.infer<typeof UpdateTimeEntrySchema>

// Update Time Entry input schema/type for API + UI payloads
export const UpdateTimeEntryInputSchema = UpdateTimeEntrySchema

export type UpdateTimeEntryInput = z.infer<typeof UpdateTimeEntryInputSchema>

/**
 * The saved outcome of a time entry write sent with an `operationId`.
 * Receipts are scoped to the workspace and actor that sent the request;
 * `entry` is null when the saved entry is no longer readable (for example,
 * it was deleted after the write). Absence of a receipt (404) means the
 * write has not committed; resending the same operation is deduplicated.
 */
export interface TimeEntryOperationReceipt {
  operationId: string
  action: 'create' | 'update'
  outcome: 'APPLIED'
  timeEntryId: string
  entry: TimeEntry | null
  recordedAt: string
}

// TimeEntry response schema
export const TimeEntryResponseSchema = z.object({
  data: TimeEntrySchema,
  message: z.string().optional(),
})

export type TimeEntryResponse = z.infer<typeof TimeEntryResponseSchema>

// TimeEntries response schema
export const TimeEntriesResponseSchema = z.object({
  data: z.array(TimeEntrySchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type TimeEntriesResponse = z.infer<typeof TimeEntriesResponseSchema>

// TimeEntryTag schemas
const TimeEntryTagUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  displayName: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
})

const TimeEntryTagActionLogSchema = z.object({
  id: z.string().uuid(),
  action: z.nativeEnum(TimeEntryTagActionEnum),
  performedBy: z.string().uuid(),
  reason: z.string().nullable().optional(),
  metadata: z.record(z.any()).nullable().optional(),
  createdAt: z.date(),
  user: TimeEntryTagUserSchema.optional(),
})

export const TimeEntryTagSchema = z.object({
  id: z.string().uuid(),
  timeEntryId: z.string().uuid(),
  taggedUserId: z.string().uuid(),
  taggerUserId: z.string().uuid(),
  organizationId: z.string().uuid(),
  status: z
    .nativeEnum(TimeEntryTagStatusEnum)
    .default(TimeEntryTagStatusEnum.ACTIVE),
  message: z.string().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  taggedUser: TimeEntryTagUserSchema.optional(),
  taggerUser: TimeEntryTagUserSchema.optional(),
  actions: z.array(TimeEntryTagActionLogSchema).optional(),
})

export type TimeEntryTag = z.infer<typeof TimeEntryTagSchema>
export type TimeEntryTagActionLog = z.infer<typeof TimeEntryTagActionLogSchema>

// Create TimeEntryTag schema
export const CreateTimeEntryTagSchema = TimeEntryTagSchema.omit({
  id: true,
  organizationId: true,
  status: true,
  createdAt: true,
  updatedAt: true,
  taggedUser: true,
  taggerUser: true,
  actions: true,
})

export type CreateTimeEntryTag = z.infer<typeof CreateTimeEntryTagSchema>

// Update TimeEntryTag schema
export const UpdateTimeEntryTagSchema = TimeEntryTagSchema.omit({
  id: true,
  timeEntryId: true,
  taggedUserId: true,
  taggerUserId: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  taggedUser: true,
  taggerUser: true,
  actions: true,
}).partial()

export type UpdateTimeEntryTag = z.infer<typeof UpdateTimeEntryTagSchema>

// TimeEntryTag response schemas
export const TimeEntryTagResponseSchema = z.object({
  data: TimeEntryTagSchema,
  message: z.string().optional(),
})

export type TimeEntryTagResponse = z.infer<typeof TimeEntryTagResponseSchema>

export const TimeEntryTagsResponseSchema = z.object({
  data: z.array(TimeEntryTagSchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type TimeEntryTagsResponse = z.infer<typeof TimeEntryTagsResponseSchema>

// =====================
// TimeEntry Approval History Schemas
// =====================

// User subset for approval history
const ApprovalHistoryUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  displayName: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
})

// Snapshot schema - stores entry state at time of action
const TimeEntrySnapshotSchema = z.object({
  description: z.string().nullable().optional(),
  duration: z.number().nullable().optional(),
  startTime: z.string().nullable().optional(), // ISO string
  endTime: z.string().nullable().optional(), // ISO string
  entryDate: z.string(), // ISO string
  billable: z.boolean(),
  moduleType: z.string().nullable().optional(),
  moduleId: z.string().nullable().optional(),
  moduleName: z.string().nullable().optional(),
  linkedModules: z.array(TimeEntryLinkedModuleSchema).optional(),
  status: z.string(),
  // Rate fields for audit trail (Phase 3+)
  roleId: z.string().uuid().nullable().optional(),
  roleSource: z.nativeEnum(TimeEntryRoleSourceEnum).nullable().optional(),
  payRate: z.number().nullable().optional(),
  billRate: z.number().nullable().optional(),
  costRate: z.number().nullable().optional(),
  resolvedFrom: z.enum(ResolvedFromValues).nullable().optional(),
})

export type TimeEntrySnapshot = z.infer<typeof TimeEntrySnapshotSchema>

// Approval history schema
export const TimeEntryApprovalHistorySchema = z.object({
  id: z.string().uuid(),
  timeEntryId: z.string().uuid(),
  organizationId: z.string().uuid(),
  action: z.nativeEnum(TimeEntryApprovalActionEnum),
  performedBy: z.string().uuid(),
  performedAt: z.date(),
  previousStatus: z.string().nullable().optional(),
  newStatus: z.string(),
  comment: z.string().nullable().optional(),
  snapshot: TimeEntrySnapshotSchema, // Snapshot of entry at this point
  performer: ApprovalHistoryUserSchema.optional(), // Include performer data
})

export type TimeEntryApprovalHistory = z.infer<
  typeof TimeEntryApprovalHistorySchema
>

// Create approval history schema (internal use)
export const CreateTimeEntryApprovalHistorySchema =
  TimeEntryApprovalHistorySchema.omit({
    id: true,
    performedAt: true,
    performer: true,
  })

export type CreateTimeEntryApprovalHistory = z.infer<
  typeof CreateTimeEntryApprovalHistorySchema
>

// Approval history response schema
export const TimeEntryApprovalHistoryResponseSchema = z.object({
  data: z.array(TimeEntryApprovalHistorySchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type TimeEntryApprovalHistoryResponse = z.infer<
  typeof TimeEntryApprovalHistoryResponseSchema
>

// =====================
// Bulk Approval Schemas
// =====================

export const BulkApprovalRequestSchema = z.object({
  timeEntryIds: z.array(z.string().uuid()),
  comment: z.string().optional(),
  draftEntryIds: z.array(z.string().uuid()).optional(),
})

export type BulkApprovalRequest = z.infer<typeof BulkApprovalRequestSchema>

export const BulkRejectionRequestSchema = z.object({
  timeEntryIds: z.array(z.string().uuid()),
  reason: z.string(),
})

export type BulkRejectionRequest = z.infer<typeof BulkRejectionRequestSchema>

export const AdminStatusChangeRequestSchema = z.object({
  status: z.nativeEnum(TimeEntryStatusEnum),
  comment: z.string().optional(),
})

export type AdminStatusChangeRequest = z.infer<
  typeof AdminStatusChangeRequestSchema
>

export const BulkAdminStatusChangeRequestSchema = z.object({
  timeEntryIds: z.array(z.string().uuid()),
  status: z.nativeEnum(TimeEntryStatusEnum),
  comment: z.string().optional(),
})

export type BulkAdminStatusChangeRequest = z.infer<
  typeof BulkAdminStatusChangeRequestSchema
>

export const BulkApprovalResponseSchema = z.object({
  success: z.array(z.string().uuid()),
  failed: z.array(
    z.object({
      id: z.string().uuid(),
      error: z.string(),
    }),
  ),
  message: z.string(),
})

export type BulkApprovalResponse = z.infer<typeof BulkApprovalResponseSchema>

// =====================
// Time entry overlap / double-tracking conflicts
// =====================
// A lightweight projection of an entry that participates in an overlap, plus the
// grouping of mutually-overlapping entries. Used by the conflict warning surfaces
// and the resolve dialog (My Time, All Timesheets, Timesheet detail).
export interface TimeConflictEntry {
  id: string
  userId: string
  entryDate: string | Date
  startTime: string | Date | null
  endTime: string | Date | null
  duration: number | null
  description: string | null
  status: string
  billable: boolean
  moduleType: string | null
  moduleId: string | null
  moduleName: string | null
}

export interface TimeConflictGroup {
  entries: TimeConflictEntry[]
}

export interface TimeConflictsResponse {
  groups: TimeConflictGroup[]
}

// =====================
// Time Log Row (persisted "Log Time" grid rows)
// =====================
// A TimeLogRow remembers a row a user added to their My Time > Log grid before
// any hours are entered: either a fully blank placeholder, or a row where a
// module/role has been picked but no time logged yet. Rows are scoped to a pay
// period when one exists, otherwise to the start of the ISO week. Once a time
// entry exists for the row's module, the entry drives the grid and the saved
// row is cleaned up.

// Scope identifies which pay period (or week) a saved row belongs to. Exactly
// one of the two fields is set.
export const TimeLogRowScopeSchema = z
  .object({
    payPeriodId: z.string().uuid().optional(),
    weekStartDate: z.string().optional(), // yyyy-MM-dd, used when no pay period
  })
  .refine(scope => Boolean(scope.payPeriodId) || Boolean(scope.weekStartDate), {
    message: 'A saved row scope requires either payPeriodId or weekStartDate',
  })

export type TimeLogRowScope = z.infer<typeof TimeLogRowScopeSchema>

export const TimeLogRowSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  userId: z.string().uuid(),
  payPeriodId: z.string().uuid().nullable().optional(),
  weekStartDate: z.union([z.string(), z.date()]).nullable().optional(),
  moduleType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  moduleName: z.string().nullable().optional(),
  moduleIdentifier: z.string().nullable().optional(),
  roleId: z.string().uuid().nullable().optional(),
  roleName: z.string().nullable().optional(),
  entityId: z.string().uuid().nullable().optional(),
  entityName: z.string().nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  projectName: z.string().nullable().optional(),
  sortOrder: z.number().int().optional(),
  createdAt: z.union([z.string(), z.date()]).optional(),
  updatedAt: z.union([z.string(), z.date()]).optional(),
})

export type TimeLogRow = z.infer<typeof TimeLogRowSchema>

// Shared shape of the editable row fields (everything except scope/identity).
const TimeLogRowFieldsSchema = z.object({
  moduleType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  moduleName: z.string().nullable().optional(),
  moduleIdentifier: z.string().nullable().optional(),
  roleId: z.string().uuid().nullable().optional(),
  roleName: z.string().nullable().optional(),
  entityId: z.string().uuid().nullable().optional(),
  entityName: z.string().nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  projectName: z.string().nullable().optional(),
  sortOrder: z.number().int().optional(),
})

export const CreateTimeLogRowSchema = TimeLogRowFieldsSchema.extend({
  payPeriodId: z.string().uuid().optional(),
  weekStartDate: z.string().optional(),
}).refine(data => Boolean(data.payPeriodId) || Boolean(data.weekStartDate), {
  message: 'A saved row requires either payPeriodId or weekStartDate',
})

export type CreateTimeLogRow = z.infer<typeof CreateTimeLogRowSchema>

export const UpdateTimeLogRowSchema = TimeLogRowFieldsSchema.partial()

export type UpdateTimeLogRow = z.infer<typeof UpdateTimeLogRowSchema>
